/**
 * Chart drawing tools.
 *
 * Drawings are anchored in **(time, price)**, never in pixels and never in bar
 * indices. That is the difference between a trendline that stays on the swing
 * it was drawn against and one that slides off as soon as you zoom, pan, or
 * switch from 5m to 1h. Converting to pixels happens once per point per frame,
 * through the same viewport the candles use.
 *
 * The layer owns its own pointer state machine and gets first refusal on every
 * pointer event; the pan/zoom handler only sees events the layer declines.
 * Without that ordering, starting a trendline would also drag the chart.
 */

import { crisp } from '../lib/frame';
import { hm, dayMonth, price as fmtPrice } from '../lib/num';
import { C, F } from '../styles/palette';
import { withAlpha } from '../data/indicators';
import type { KlineSeries } from '../data/klines';
import type { ChartViewport } from './scales';

export type DrawTool =
  | 'cursor'
  | 'trend'
  | 'ray'
  | 'hline'
  | 'vline'
  | 'rect'
  | 'fib'
  | 'measure';

export interface DrawPoint {
  time: number;
  price: number;
}

export interface Drawing {
  id: string;
  kind: Exclude<DrawTool, 'cursor'>;
  points: DrawPoint[];
  color: string;
  width: number;
  locked: boolean;
}

export const TOOLS: { tool: DrawTool; label: string; glyph: string; hint: string; points: number }[] =
  [
    { tool: 'cursor', label: 'Cursor', glyph: '⌖', hint: 'Select and move (V)', points: 0 },
    { tool: 'trend', label: 'Trend line', glyph: '╱', hint: 'Two-point segment (T)', points: 2 },
    { tool: 'ray', label: 'Ray', glyph: '↗', hint: 'Extends to the right (R)', points: 2 },
    { tool: 'hline', label: 'Horizontal', glyph: '─', hint: 'Price level (H)', points: 1 },
    { tool: 'vline', label: 'Vertical', glyph: '│', hint: 'Time marker (I)', points: 1 },
    { tool: 'rect', label: 'Rectangle', glyph: '▭', hint: 'Zone / range (B)', points: 2 },
    { tool: 'fib', label: 'Fib retrace', glyph: '≡', hint: 'Retracement levels (F)', points: 2 },
    { tool: 'measure', label: 'Measure', glyph: '↔', hint: 'Price / time delta (M)', points: 2 },
  ];

const FIB_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1, 1.272, 1.618];

const HIT_PX = 6;
const HANDLE_PX = 4;

export interface HitContext {
  vp: ChartViewport;
  series: KlineSeries;
  /** Held shift disables magnet snapping. */
  shift: boolean;
}

interface Anchor {
  drawing: Drawing;
  /** Index of the grabbed handle, or -1 when the whole shape is grabbed. */
  handle: number;
  startPoints: DrawPoint[];
  startTime: number;
  startPrice: number;
}

let seq = 0;
const nextId = () => `d${Date.now().toString(36)}${(seq++).toString(36)}`;

export class DrawingLayer {
  drawings: Drawing[] = [];
  tool: DrawTool = 'cursor';
  color = C.accent;
  width = 1.25;
  /** Snap placement to nearby candle O/H/L/C. */
  magnet = true;

  selectedId: string | null = null;
  hoverId: string | null = null;

  /** Bumped on any change so the chart knows to repaint. */
  version = 0;

  private symbol = '';
  private pending: Drawing | null = null;
  private anchor: Anchor | null = null;
  private measureLive: Drawing | null = null;

  // -- persistence ----------------------------------------------------------

  setSymbol(symbol: string): void {
    if (this.symbol === symbol) return;
    this.save();
    this.symbol = symbol;
    this.pending = null;
    this.anchor = null;
    this.measureLive = null;
    this.selectedId = null;
    this.drawings = this.load(symbol);
    this.version++;
  }

  private key(symbol: string): string {
    return `terminal.drawings.${symbol}`;
  }

  private load(symbol: string): Drawing[] {
    try {
      const raw = localStorage.getItem(this.key(symbol));
      if (!raw) return [];
      const parsed = JSON.parse(raw) as Drawing[];
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(
        (d) => d && typeof d.id === 'string' && Array.isArray(d.points) && d.points.length > 0,
      );
    } catch {
      return [];
    }
  }

  save(): void {
    if (!this.symbol) return;
    try {
      // A live measurement is scratch work, not a saved drawing.
      const persistable = this.drawings.filter((d) => d.kind !== 'measure');
      if (persistable.length === 0) localStorage.removeItem(this.key(this.symbol));
      else localStorage.setItem(this.key(this.symbol), JSON.stringify(persistable));
    } catch {
      /* quota */
    }
  }

  // -- coordinate helpers ---------------------------------------------------

  private toPx(p: DrawPoint, ctx: HitContext): { x: number; y: number } {
    return {
      x: ctx.vp.xOf(ctx.series.fractionalIndex(p.time)),
      y: ctx.vp.yOf(p.price),
    };
  }

  private fromPx(x: number, y: number, ctx: HitContext): DrawPoint {
    const index = ctx.vp.indexAt(x);
    const time = ctx.series.timeAtIndex(index);
    let price = ctx.vp.priceAt(y);

    // Magnet: snap to the nearest O/H/L/C of the bar under the cursor when it
    // is within a few pixels. This is what makes a trendline actually touch
    // the wick it is supposed to touch.
    if (this.magnet && !ctx.shift) {
      const i = Math.round(index);
      if (i >= 0 && i < ctx.series.length) {
        const candidates = [
          ctx.series.open.get(i),
          ctx.series.high.get(i),
          ctx.series.low.get(i),
          ctx.series.close.get(i),
        ];
        let best = price;
        let bestDist = HIT_PX + 1;
        for (const c of candidates) {
          const d = Math.abs(ctx.vp.yOf(c) - y);
          if (d < bestDist) {
            bestDist = d;
            best = c;
          }
        }
        if (bestDist <= HIT_PX) price = best;
      }
    }
    return { time, price };
  }

  // -- pointer state machine ------------------------------------------------

  /** @returns true when the layer consumed the event. */
  pointerDown(x: number, y: number, ctx: HitContext, button: number): boolean {
    if (button !== 0) return false;

    // Placing a new drawing.
    if (this.tool !== 'cursor') {
      const spec = TOOLS.find((t) => t.tool === this.tool)!;
      const point = this.fromPx(x, y, ctx);

      if (this.pending) {
        this.pending.points[this.pending.points.length - 1] = point;
        if (this.pending.points.length >= spec.points) {
          this.commit(this.pending);
          this.pending = null;
          // Single-shot: drop back to the cursor so the next click selects.
          this.tool = 'cursor';
        }
        this.version++;
        return true;
      }

      const d: Drawing = {
        id: nextId(),
        kind: this.tool,
        points: spec.points === 1 ? [point] : [point, point],
        color: this.tool === 'measure' ? C.info : this.color,
        width: this.width,
        locked: false,
      };
      if (spec.points === 1) {
        this.commit(d);
        this.tool = 'cursor';
      } else {
        this.pending = d;
      }
      this.version++;
      return true;
    }

    // Cursor mode: grab a handle, then a body.
    const hitHandle = this.hitHandle(x, y, ctx);
    if (hitHandle) {
      this.selectedId = hitHandle.drawing.id;
      this.anchor = {
        drawing: hitHandle.drawing,
        handle: hitHandle.index,
        startPoints: hitHandle.drawing.points.map((p) => ({ ...p })),
        startTime: 0,
        startPrice: 0,
      };
      this.version++;
      return true;
    }

    const hit = this.hitTest(x, y, ctx);
    if (hit) {
      this.selectedId = hit.id;
      if (!hit.locked) {
        const p = this.fromPx(x, y, ctx);
        this.anchor = {
          drawing: hit,
          handle: -1,
          startPoints: hit.points.map((q) => ({ ...q })),
          startTime: p.time,
          startPrice: p.price,
        };
      }
      this.version++;
      return true;
    }

    if (this.selectedId) {
      this.selectedId = null;
      this.version++;
      // Deselecting should not also start a pan — swallow this one click.
      return true;
    }
    return false;
  }

  pointerMove(x: number, y: number, ctx: HitContext): boolean {
    if (this.pending) {
      this.pending.points[this.pending.points.length - 1] = this.fromPx(x, y, ctx);
      this.version++;
      return true;
    }

    if (this.anchor) {
      const a = this.anchor;
      if (a.handle >= 0) {
        a.drawing.points[a.handle] = this.fromPx(x, y, ctx);
      } else {
        // Translate: apply the pointer delta in data space to every point.
        const p = this.fromPx(x, y, ctx);
        const dt = p.time - a.startTime;
        const dp = p.price - a.startPrice;
        for (let i = 0; i < a.drawing.points.length; i++) {
          a.drawing.points[i] = {
            time: a.startPoints[i].time + dt,
            price: a.startPoints[i].price + dp,
          };
        }
      }
      this.version++;
      return true;
    }

    // Hover feedback only — never consumes, so the crosshair keeps tracking.
    const hit = this.hitTest(x, y, ctx);
    const id = hit ? hit.id : null;
    if (id !== this.hoverId) {
      this.hoverId = id;
      this.version++;
    }
    return false;
  }

  pointerUp(): boolean {
    if (this.anchor) {
      this.anchor = null;
      this.save();
      this.version++;
      return true;
    }
    return false;
  }

  /** Escape / Delete handling. @returns true when consumed. */
  keyDown(key: string): boolean {
    if (key === 'Escape') {
      if (this.pending) {
        this.pending = null;
        this.tool = 'cursor';
        this.version++;
        return true;
      }
      if (this.measureLive) {
        this.measureLive = null;
        this.version++;
        return true;
      }
      if (this.selectedId) {
        this.selectedId = null;
        this.version++;
        return true;
      }
      if (this.tool !== 'cursor') {
        this.tool = 'cursor';
        this.version++;
        return true;
      }
      return false;
    }
    if (key === 'Delete' || key === 'Backspace') {
      return this.deleteSelected();
    }
    return false;
  }

  private commit(d: Drawing): void {
    if (d.kind === 'measure') {
      // Measurements replace one another rather than accumulating.
      this.drawings = this.drawings.filter((x) => x.kind !== 'measure');
      this.measureLive = d;
    }
    this.drawings.push(d);
    this.selectedId = d.kind === 'measure' ? null : d.id;
    this.save();
  }

  deleteSelected(): boolean {
    if (!this.selectedId) return false;
    const before = this.drawings.length;
    this.drawings = this.drawings.filter((d) => d.id !== this.selectedId);
    this.selectedId = null;
    if (this.drawings.length !== before) {
      this.save();
      this.version++;
      return true;
    }
    return false;
  }

  clearAll(): void {
    this.drawings = [];
    this.selectedId = null;
    this.pending = null;
    this.measureLive = null;
    this.save();
    this.version++;
  }

  setTool(tool: DrawTool): void {
    if (this.tool === tool) return;
    this.tool = tool;
    this.pending = null;
    if (tool !== 'cursor') this.selectedId = null;
    this.version++;
  }

  get count(): number {
    return this.drawings.filter((d) => d.kind !== 'measure').length;
  }

  get isPlacing(): boolean {
    return this.pending !== null;
  }

  // -- hit testing ----------------------------------------------------------

  private hitTest(x: number, y: number, ctx: HitContext): Drawing | null {
    // Topmost first: later drawings are painted over earlier ones.
    for (let i = this.drawings.length - 1; i >= 0; i--) {
      const d = this.drawings[i];
      if (this.hitOne(d, x, y, ctx)) return d;
    }
    return null;
  }

  private hitOne(d: Drawing, x: number, y: number, ctx: HitContext): boolean {
    const pts = d.points.map((p) => this.toPx(p, ctx));
    switch (d.kind) {
      case 'hline':
        return Math.abs(y - pts[0].y) <= HIT_PX;
      case 'vline':
        return Math.abs(x - pts[0].x) <= HIT_PX;
      case 'trend':
      case 'measure':
        return distToSegment(x, y, pts[0].x, pts[0].y, pts[1].x, pts[1].y) <= HIT_PX;
      case 'ray': {
        // Extend the segment to the right edge before testing.
        const ext = extendRay(pts[0], pts[1], ctx.vp.plotRight);
        return distToSegment(x, y, pts[0].x, pts[0].y, ext.x, ext.y) <= HIT_PX;
      }
      case 'rect':
      case 'fib': {
        const x0 = Math.min(pts[0].x, pts[1].x);
        const x1 = Math.max(pts[0].x, pts[1].x);
        const y0 = Math.min(pts[0].y, pts[1].y);
        const y1 = Math.max(pts[0].y, pts[1].y);
        // Edge hit only, so a large zone does not swallow every click inside it.
        const nearX = (x >= x0 - HIT_PX && x <= x1 + HIT_PX);
        const nearY = (y >= y0 - HIT_PX && y <= y1 + HIT_PX);
        const onVert = nearY && (Math.abs(x - x0) <= HIT_PX || Math.abs(x - x1) <= HIT_PX);
        const onHorz = nearX && (Math.abs(y - y0) <= HIT_PX || Math.abs(y - y1) <= HIT_PX);
        return onVert || onHorz;
      }
    }
  }

  private hitHandle(
    x: number,
    y: number,
    ctx: HitContext,
  ): { drawing: Drawing; index: number } | null {
    if (!this.selectedId) return null;
    const d = this.drawings.find((k) => k.id === this.selectedId);
    if (!d || d.locked) return null;
    for (let i = 0; i < d.points.length; i++) {
      const p = this.toPx(d.points[i], ctx);
      if (Math.abs(p.x - x) <= HANDLE_PX + 2 && Math.abs(p.y - y) <= HANDLE_PX + 2) {
        return { drawing: d, index: i };
      }
    }
    return null;
  }

  /** CSS cursor the chart should show, given the layer's current state. */
  cssCursor(): string {
    if (this.tool !== 'cursor') return 'crosshair';
    if (this.anchor) return 'grabbing';
    if (this.hoverId) return 'pointer';
    return 'crosshair';
  }

  // -- rendering ------------------------------------------------------------

  render(ctx2d: CanvasRenderingContext2D, ctx: HitContext, precision: number): void {
    const all = this.pending ? [...this.drawings, this.pending] : this.drawings;
    if (all.length === 0) return;

    ctx2d.save();
    ctx2d.beginPath();
    ctx2d.rect(ctx.vp.plotLeft, ctx.vp.bodyTop, ctx.vp.plotWidth, ctx.vp.bodyHeight);
    ctx2d.clip();
    ctx2d.lineJoin = 'round';
    ctx2d.lineCap = 'round';

    for (const d of all) {
      const selected = d.id === this.selectedId;
      const hovered = d.id === this.hoverId;
      this.renderOne(ctx2d, d, ctx, precision, selected, hovered);
    }

    ctx2d.restore();

    // Handles are drawn outside the clip so an off-screen endpoint still shows
    // its handle at the edge rather than vanishing.
    const sel = all.find((d) => d.id === this.selectedId);
    if (sel && !sel.locked) this.renderHandles(ctx2d, sel, ctx);
  }

  private renderOne(
    g: CanvasRenderingContext2D,
    d: Drawing,
    ctx: HitContext,
    precision: number,
    selected: boolean,
    hovered: boolean,
  ): void {
    const vp = ctx.vp;
    const pts = d.points.map((p) => this.toPx(p, ctx));
    const emphasis = selected ? 1 : hovered ? 0.85 : 0.72;

    g.strokeStyle = selected ? C.accent : d.color;
    g.lineWidth = d.width + (selected ? 0.75 : 0);
    g.globalAlpha = emphasis;

    switch (d.kind) {
      case 'hline': {
        const y = crisp(pts[0].y);
        g.beginPath();
        g.moveTo(vp.plotLeft, y);
        g.lineTo(vp.plotRight, y);
        g.stroke();
        g.globalAlpha = 1;
        this.axisTag(g, vp, y, fmtPrice(d.points[0].price, precision), d.color);
        break;
      }
      case 'vline': {
        const x = crisp(pts[0].x);
        g.beginPath();
        g.moveTo(x, vp.bodyTop);
        g.lineTo(x, vp.bodyBottom);
        g.stroke();
        g.globalAlpha = 1;
        this.timeTag(g, vp, x, d.points[0].time, ctx.series.intervalMs);
        break;
      }
      case 'trend': {
        g.beginPath();
        g.moveTo(pts[0].x, pts[0].y);
        g.lineTo(pts[1].x, pts[1].y);
        g.stroke();
        break;
      }
      case 'ray': {
        const ext = extendRay(pts[0], pts[1], vp.plotRight);
        g.beginPath();
        g.moveTo(pts[0].x, pts[0].y);
        g.lineTo(ext.x, ext.y);
        g.stroke();
        break;
      }
      case 'rect': {
        const x = Math.min(pts[0].x, pts[1].x);
        const y = Math.min(pts[0].y, pts[1].y);
        const w = Math.abs(pts[1].x - pts[0].x);
        const h = Math.abs(pts[1].y - pts[0].y);
        g.fillStyle = withAlpha(d.color, 0.1);
        g.fillRect(x, y, w, h);
        g.strokeRect(crisp(x), crisp(y), Math.round(w), Math.round(h));
        break;
      }
      case 'fib':
        this.renderFib(g, d, pts, vp, precision);
        break;
      case 'measure':
        this.renderMeasure(g, d, pts, ctx, precision);
        break;
    }
    g.globalAlpha = 1;
  }

  private renderFib(
    g: CanvasRenderingContext2D,
    d: Drawing,
    pts: { x: number; y: number }[],
    vp: ChartViewport,
    precision: number,
  ): void {
    const p0 = d.points[0].price;
    const p1 = d.points[1].price;
    const x0 = Math.min(pts[0].x, pts[1].x);
    const x1 = Math.max(pts[0].x, pts[1].x);

    g.font = F.micro;
    g.textBaseline = 'bottom';
    g.textAlign = 'left';

    let prevY = NaN;
    for (let i = 0; i < FIB_LEVELS.length; i++) {
      const lv = FIB_LEVELS[i];
      const p = p0 + (p1 - p0) * lv;
      const y = vp.yOf(p);

      // Alternating band fill between adjacent levels reads much faster than
      // nine bare lines.
      if (!Number.isNaN(prevY) && i % 2 === 1) {
        g.fillStyle = withAlpha(d.color, 0.05);
        g.fillRect(x0, Math.min(prevY, y), x1 - x0, Math.abs(y - prevY));
      }
      prevY = y;

      g.strokeStyle = lv === 0 || lv === 1 ? d.color : withAlpha(d.color, 0.55);
      g.lineWidth = lv === 0 || lv === 1 ? d.width : 1;
      g.beginPath();
      g.moveTo(x0, crisp(y));
      g.lineTo(x1, crisp(y));
      g.stroke();

      g.fillStyle = C.text2;
      g.fillText(`${(lv * 100).toFixed(1)}%`, x0 + 3, y - 2);
      g.textAlign = 'right';
      g.fillStyle = withAlpha(d.color, 0.9);
      g.fillText(fmtPrice(p, precision), x1 - 3, y - 2);
      g.textAlign = 'left';
    }

    // The anchor leg itself, so it is clear which swing was measured.
    g.strokeStyle = withAlpha(d.color, 0.5);
    g.setLineDash([3, 3]);
    g.lineWidth = 1;
    g.beginPath();
    g.moveTo(pts[0].x, pts[0].y);
    g.lineTo(pts[1].x, pts[1].y);
    g.stroke();
    g.setLineDash([]);
  }

  private renderMeasure(
    g: CanvasRenderingContext2D,
    d: Drawing,
    pts: { x: number; y: number }[],
    ctx: HitContext,
    precision: number,
  ): void {
    const a = d.points[0];
    const b = d.points[1];
    const up = b.price >= a.price;
    const col = up ? C.up : C.down;

    const x = Math.min(pts[0].x, pts[1].x);
    const y = Math.min(pts[0].y, pts[1].y);
    const w = Math.abs(pts[1].x - pts[0].x);
    const h = Math.abs(pts[1].y - pts[0].y);

    g.fillStyle = withAlpha(col, 0.12);
    g.fillRect(x, y, w, h);
    g.strokeStyle = withAlpha(col, 0.8);
    g.lineWidth = 1;
    g.strokeRect(crisp(x), crisp(y), Math.round(w), Math.round(h));

    // Arrow along the price axis
    g.beginPath();
    g.moveTo(crisp(pts[1].x), pts[0].y);
    g.lineTo(crisp(pts[1].x), pts[1].y);
    g.stroke();

    const delta = b.price - a.price;
    const pctChange = a.price !== 0 ? (delta / a.price) * 100 : 0;
    const bars = Math.abs(
      Math.round(ctx.series.fractionalIndex(b.time) - ctx.series.fractionalIndex(a.time)),
    );
    const ms = Math.abs(b.time - a.time);
    const lines = [
      `${delta >= 0 ? '+' : ''}${fmtPrice(delta, precision)}  (${pctChange >= 0 ? '+' : ''}${pctChange.toFixed(2)}%)`,
      `${bars} bars   ${formatSpan(ms)}`,
    ];

    g.font = F.xs;
    let boxW = 0;
    for (const l of lines) boxW = Math.max(boxW, g.measureText(l).width);
    boxW += 12;
    const boxH = 30;
    const bx = Math.min(ctx.vp.plotRight - boxW - 2, Math.max(ctx.vp.plotLeft + 2, x + w / 2 - boxW / 2));
    const by = up ? Math.max(ctx.vp.bodyTop + 2, y - boxH - 4) : Math.min(ctx.vp.bodyBottom - boxH - 2, y + h + 4);

    g.fillStyle = 'rgba(7,9,12,0.92)';
    g.fillRect(bx, by, boxW, boxH);
    g.strokeStyle = withAlpha(col, 0.7);
    g.strokeRect(crisp(bx), crisp(by), Math.round(boxW), boxH);
    g.textAlign = 'left';
    g.textBaseline = 'top';
    g.fillStyle = col;
    g.fillText(lines[0], bx + 6, by + 5);
    g.fillStyle = C.text2;
    g.fillText(lines[1], bx + 6, by + 17);
  }

  private renderHandles(g: CanvasRenderingContext2D, d: Drawing, ctx: HitContext): void {
    g.fillStyle = C.bg0;
    g.strokeStyle = C.accent;
    g.lineWidth = 1.25;
    for (const p of d.points) {
      const px = this.toPx(p, ctx);
      g.beginPath();
      g.rect(
        Math.round(px.x) - HANDLE_PX,
        Math.round(px.y) - HANDLE_PX,
        HANDLE_PX * 2,
        HANDLE_PX * 2,
      );
      g.fill();
      g.stroke();
    }
  }

  private axisTag(
    g: CanvasRenderingContext2D,
    vp: ChartViewport,
    y: number,
    text: string,
    color: string,
  ): void {
    if (y < vp.bodyTop || y > vp.bodyBottom) return;
    const h = 14;
    g.fillStyle = color;
    g.fillRect(vp.plotRight + 1, Math.round(y - h / 2), vp.insets.right - 1, h);
    g.font = F.micro;
    g.fillStyle = '#05080b';
    g.textAlign = 'left';
    g.textBaseline = 'middle';
    g.fillText(text, vp.plotRight + 5, y + 0.5);
  }

  private timeTag(
    g: CanvasRenderingContext2D,
    vp: ChartViewport,
    x: number,
    time: number,
    intervalMs: number,
  ): void {
    const txt = intervalMs >= 86_400_000 ? dayMonth(time) : hm(time);
    g.font = F.micro;
    const w = g.measureText(txt).width + 10;
    const tx = Math.max(0, Math.min(vp.width - w, x - w / 2));
    g.fillStyle = C.bg3;
    g.fillRect(tx, vp.bodyBottom + 1, w, vp.insets.bottom - 2);
    g.fillStyle = C.text1;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(txt, tx + w / 2, vp.bodyBottom + vp.insets.bottom / 2);
  }
}

// ---------------------------------------------------------------------------

function distToSegment(
  px: number,
  py: number,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

function extendRay(
  a: { x: number; y: number },
  b: { x: number; y: number },
  rightEdge: number,
): { x: number; y: number } {
  const dx = b.x - a.x;
  if (Math.abs(dx) < 1e-6) return { x: b.x, y: b.y > a.y ? 1e5 : -1e5 };
  const slope = (b.y - a.y) / dx;
  // Extend in whichever horizontal direction the second point lies.
  const targetX = dx > 0 ? rightEdge + 40 : -40;
  return { x: targetX, y: a.y + slope * (targetX - a.x) };
}

function formatSpan(ms: number): string {
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(0)}s`;
  const m = s / 60;
  if (m < 60) return `${m.toFixed(0)}m`;
  const h = m / 60;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
}
